import { useRef, useState, useEffect, useCallback } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { USE_HARDCODED_CONVERSATION, loadConversation } from "../api.js";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
import { processConversationData } from "../utils/conversationProcessor.js";
const useConversationLoader = ({
  conversationId,
  onConversationLoaded,
  onConversationNotFound,
  isActivelyStreaming = false,
  conversationHistoryLength = 0,
  apiBaseUrl
}) => {
  const queryClient = useQueryClient();
  const loadedConversationIdRef = useRef(null);
  const loadingConversationIdRef = useRef(null);
  const [forceReload, setForceReload] = useState(0);
  const previousConversationIdRef = useRef(null);
  const wasStreamingRef = useRef(false);
  const [isPendingAssistantResponse, setIsPendingAssistantResponse] = useState(false);
  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
  const pollingStartTimeRef = useRef(null);
  const POLLING_INTERVAL_MS = 3e3;
  const POLLING_TIMEOUT_MS = 18e4;
  useEffect(() => {
    setIsPendingAssistantResponse(false);
    setIsPollingTimedOut(false);
    pollingStartTimeRef.current = null;
  }, [conversationId]);
  useEffect(() => {
    const prevId = previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;
    if (isActivelyStreaming) {
      if (conversationId || USE_HARDCODED_CONVERSATION) {
        loadedConversationIdRef.current = conversationId || "hardcoded";
      }
      return;
    }
    if (wasStreamingRef.current && !isActivelyStreaming) {
      if (conversationId || USE_HARDCODED_CONVERSATION) {
        loadedConversationIdRef.current = conversationId || "hardcoded";
      }
      return;
    }
    if (conversationId && conversationId !== loadedConversationIdRef.current) {
      if (conversationHistoryLength > 0) {
        loadedConversationIdRef.current = conversationId;
        return;
      }
      if (loadingConversationIdRef.current === conversationId) {
        return;
      }
      loadingConversationIdRef.current = conversationId;
      setForceReload((prev) => prev + 1);
      queryClient.invalidateQueries({
        queryKey: ["conversation", conversationId]
      });
    } else if (conversationId && prevId === null && conversationId === loadedConversationIdRef.current) {
      loadedConversationIdRef.current = null;
      setForceReload((prev) => prev + 1);
      queryClient.invalidateQueries({
        queryKey: ["conversation", conversationId]
      });
    }
  }, [conversationId, queryClient, isActivelyStreaming, conversationHistoryLength, forceReload]);
  useEffect(() => {
    wasStreamingRef.current = isActivelyStreaming;
  }, [isActivelyStreaming]);
  const {
    data: loadedConversationData,
    isLoading: isLoadingConversationQuery,
    isSuccess: isConversationQuerySuccess,
    error: conversationLoadError
  } = useQuery({
    queryKey: ["conversation", conversationId, forceReload, USE_HARDCODED_CONVERSATION],
    queryFn: async () => {
      if (!conversationId) {
        return null;
      }
      const headers = await getHeadersWithUniqueVisitorId();
      return loadConversation({
        conversationId,
        headersToInject: headers,
        apiBaseUrl
      });
    },
    enabled: !!conversationId && !isActivelyStreaming && conversationId !== loadedConversationIdRef.current || isPendingAssistantResponse && !isPollingTimedOut && !!conversationId && !isActivelyStreaming,
    retry: false,
    staleTime: Infinity,
    refetchInterval: isPendingAssistantResponse && !isPollingTimedOut ? POLLING_INTERVAL_MS : false
  });
  useEffect(() => {
    const shouldProcess = loadedConversationData && conversationId;
    if (shouldProcess) {
      const effectiveConversationId = conversationId || (loadedConversationData == null ? void 0 : loadedConversationData.conversationId) || "hardcoded";
      if (loadedConversationData.error === "unauthorized") {
        console.warn(`Unauthorized to access conversation ${effectiveConversationId}, redirecting`);
        onConversationNotFound();
        loadedConversationIdRef.current = effectiveConversationId;
        loadingConversationIdRef.current = null;
        if (typeof window !== "undefined") {
          window.history.pushState({}, "", "/concierge");
        }
        return;
      }
      const {
        messages: processedMessages,
        productMap,
        surveyMap,
        followUpQuestions,
        previewSearchQueries,
        previewQueryProducts
      } = processConversationData(loadedConversationData);
      if (processedMessages.length > 0) {
        const sharingInfo = {
          isSharedView: loadedConversationData.isSharedView,
          sharedAtMessageIndex: loadedConversationData.sharedAtMessageIndex,
          sharedByUserName: loadedConversationData.sharedByUserName,
          sourceConversationId: loadedConversationData.sourceConversationId,
          sourceCheckpointIndex: loadedConversationData.sourceCheckpointIndex
        };
        onConversationLoaded(
          processedMessages,
          productMap,
          surveyMap,
          followUpQuestions,
          previewSearchQueries,
          previewQueryProducts,
          sharingInfo
        );
        loadedConversationIdRef.current = effectiveConversationId;
        loadingConversationIdRef.current = null;
        const lastProcessedMessage = processedMessages[processedMessages.length - 1];
        if ((lastProcessedMessage == null ? void 0 : lastProcessedMessage.role) === "user") {
          if (!pollingStartTimeRef.current) {
            pollingStartTimeRef.current = Date.now();
          }
          setIsPendingAssistantResponse(true);
        } else {
          setIsPendingAssistantResponse(false);
          setIsPollingTimedOut(false);
          pollingStartTimeRef.current = null;
        }
      } else {
        console.warn(`Conversation ${effectiveConversationId} not found, clearing URL`);
        onConversationNotFound();
        loadedConversationIdRef.current = effectiveConversationId;
        loadingConversationIdRef.current = null;
      }
    } else if (isConversationQuerySuccess && loadedConversationData === null && conversationId && loadedConversationIdRef.current !== conversationId) {
      console.warn(`Conversation ${conversationId} not found (null response), clearing URL`);
      onConversationNotFound();
      loadedConversationIdRef.current = conversationId;
      loadingConversationIdRef.current = null;
    }
  }, [loadedConversationData, conversationId, isConversationQuerySuccess]);
  useEffect(() => {
    if (conversationLoadError && conversationId) {
      console.error("Failed to load conversation:", conversationLoadError);
      onConversationNotFound();
      loadedConversationIdRef.current = conversationId;
      loadingConversationIdRef.current = null;
    }
  }, [conversationLoadError, conversationId]);
  useEffect(() => {
    if (!isPendingAssistantResponse || isPollingTimedOut) return;
    const timer = setTimeout(() => {
      setIsPollingTimedOut(true);
      setIsPendingAssistantResponse(false);
    }, POLLING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isPendingAssistantResponse, isPollingTimedOut]);
  const resetPendingState = useCallback(() => {
    setIsPendingAssistantResponse(false);
    setIsPollingTimedOut(false);
    pollingStartTimeRef.current = null;
  }, []);
  return {
    isLoadingConversationQuery,
    conversationLoadError,
    isPendingAssistantResponse,
    isPollingTimedOut,
    resetPendingState
  };
};
export {
  useConversationLoader
};
//# sourceMappingURL=useConversationLoader.js.map
